import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useCreateInvoice, useCreditableInvoices } from '~/api/invoices';
import { useCreateQuotation } from '~/api/quotations';
import { useUser } from '~/api/user';
import { Container, Header, Menu } from '~/components';
import { productFruitsIds } from '~/constants/productFruits';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { getGreetingMessage } from '~/utils/dates';
import { toast } from '~/utils/toast';

import { AccountCompletion } from './AccountCompletion/AccountCompletion';
import { Charts } from './Charts/Charts';
import { dashboardGridStyle } from './Dashboard.css';
import { ForumPostsSummary } from './ForumPostsSummary/ForumPostsSummary';
import { JobPostsSummary } from './JobPostsSummary/JobPostsSummary';
import { OutstandingInvoicesCard } from './OutstandingInvoicesCard/OutstandingInvoicesCard';
import { StatsSummary } from './StatsSummary/StatsSummary';

export const Dashboard = () => {
  const { data: user } = useUser();
  const { data: creditableInvoices } = useCreditableInvoices();
  const createDraftInvoiceMutation = useCreateInvoice();
  const createDraftQuotationMutation = useCreateQuotation();

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'dashboard']);

  const createDraftInvoice = useCallback(() => {
    createDraftInvoiceMutation.mutate(undefined, {
      onSuccess: ({ id }) => navigate(r(routes.createInvoice, { invoiceId: id })),
      onError: () => toast.danger({ description: t('common:error') }),
    });
  }, [createDraftInvoiceMutation, navigate, t]);

  const createDraftQuotation = () => {
    createDraftQuotationMutation.mutate(undefined, {
      onSuccess: ({ id }) => navigate(r(routes.createQuotation, { quotationId: id })),
      onError: () => toast.danger({ description: t('common:error') }),
    });
  };

  return (
    <>
      <Header.Root>
        <Header.Content title={`${getGreetingMessage()} ${user.firstName}`} />

        <Header.Actions>
          <Header.ActionGroup floatingIcon="Add" loading={createDraftInvoiceMutation.isPending || createDraftQuotationMutation.isPending}>
            <Header.Action
              data-pf-id={productFruitsIds.dashboard.header.createInvoice}
              icon="Add"
              loading={createDraftInvoiceMutation.isPending}
              onClick={createDraftInvoice}
            >
              {t('dashboard:header.actionBlock.button')}
            </Header.Action>

            <Header.ActionGroupMenu loading={createDraftQuotationMutation.isPending}>
              <Menu.Item disabled={createDraftQuotationMutation.isPending} icon="Add" onClick={createDraftQuotation}>
                {t('dashboard:header.actionBlock.dropdown.quotation')}
              </Menu.Item>

              {creditableInvoices.length > 0 && (
                <Menu.LinkItem icon="Add" to={routes.createCreditNote}>
                  {t('dashboard:header.actionBlock.dropdown.creditnote')}
                </Menu.LinkItem>
              )}

              <Menu.LinkItem icon="Add" to={routes.createClient}>
                {t('dashboard:header.actionBlock.dropdown.client')}
              </Menu.LinkItem>
            </Header.ActionGroupMenu>
          </Header.ActionGroup>
        </Header.Actions>
      </Header.Root>

      <Container>
        <div className={dashboardGridStyle}>
          <StatsSummary />

          <AccountCompletion createInvoice={createDraftInvoice} isCreatingInvoice={createDraftInvoiceMutation.isPending} />

          <OutstandingInvoicesCard />

          <Charts />

          <JobPostsSummary />

          <ForumPostsSummary />
        </div>
      </Container>
    </>
  );
};
